<script setup lang="ts">
import { useMyCollections, CollectionEditOwnDialog, CreateCollectionButton } from '@ilteducation/content-collections';
import config from '@/config/ConfigProvider';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useLocale } from '@/plugins/translation';
import HorizontalContentList from '@/components/collections/HorizontalContentList.vue';
import SearchInput from '@/components/collections/SearchInput.vue';
import { computed, ref } from 'vue';
import { useDialogFocus } from '@ilteducation/components';
import useSnackbar from '@/composables/use-snackbar';
import { useActivityStream } from '@/composables/use-activity-stream';
import { useRouter, useRoute } from 'vue-router';

const { t, l } = useLocale();

const context = config.value('context');
const router = useRouter();
const route = useRoute();
const { collections } = useMyCollections(context, 100, undefined);
const { snackbarSuccess } = useSnackbar();
const { logCreateCollection } = useActivityStream();

const searchValue = ref<string>('');

const showCreateCollectionDialog = ref(false);
const { rememberFocus } = useDialogFocus([showCreateCollectionDialog]);

const filteredCollections = computed(() => {
  if (!searchValue.value.trim()) {
    return collections.value.slice().sort((a, b) => a.name.localeCompare(b.name));
  }
  return collections.value
    .filter((collection) => {
      return collection.name.toLowerCase().includes(searchValue.value.toLowerCase());
    })
    .sort((a, b) => a.name.localeCompare(b.name));
});

const handleShowCreateCollectionDialog = () => {
  showCreateCollectionDialog.value = true;
  rememberFocus();
};

const handleCreateCollection = async (id: string) => {
  snackbarSuccess(t(l.content_collections.message_created));
  logCreateCollection(route.fullPath);
  router.push({ name: 'collection', params: { id } });
};

const onSearch = (value: string) => {
  searchValue.value = value;
};

const onClearSearch = () => {
  searchValue.value = '';
};
</script>

<template>
  <v-container class="custom-container pa-4">
    <v-row class="mb-4">
      <v-col cols="8">
        <SearchInput @do-search="onSearch" @clear-search="onClearSearch"></SearchInput>
      </v-col>
      <v-col>
        <CreateCollectionButton @on-create-collection="handleShowCreateCollectionDialog" rounded-class="rounded-lg">
          <template #prepend-icon>
            <FontAwesomeIcon class="mr-2" icon="fa-solid fa-rectangle-history-circle-plus" />
          </template>
        </CreateCollectionButton>
        <CollectionEditOwnDialog
          v-if="showCreateCollectionDialog"
          :context="context"
          :selected-icon="'thinPhotoFilm'"
          :show-icons="false"
          @create-collection="handleCreateCollection"
          @close="showCreateCollectionDialog = false"
        >
        </CollectionEditOwnDialog>
      </v-col>
    </v-row>
    <v-row v-for="collection in filteredCollections" :key="collection.id" no-gutters class="flex-column">
      <v-col>
        <div class="d-flex align-center">
          <h2 class="collection-title mr-8">{{ collection.name }}</h2>
          <v-btn color="on-surface-medium" variant="text" class="text-none" :to="`collection/${collection.id}`">
            {{ t(l.show_more) }}
            <template #append>
              <FontAwesomeIcon icon="fa-regular fa-chevron-right" />
            </template>
          </v-btn>
        </div>
      </v-col>
      <v-col class="pl-2 pt-2">
        <HorizontalContentList :collection="collection" />
      </v-col>
    </v-row>
    <template v-if="filteredCollections.length === 0 && searchValue">
      {{ t(l.label_no_matches) }}
    </template>
    <template v-if="filteredCollections.length === 0 && !searchValue">
      {{ t(l.content_collections.no_collections) }}
    </template>
  </v-container>
</template>

<style scoped lang="scss">
.collection-title {
  color: rgb(var(--v-theme-on-surface-medium));
  font-weight: 500;
}
</style>
